import {type FirebaseTimestamp, type RawTimestampData} from '../firebase/types';
import {getTimestampSeconds, getTimestampNanoseconds} from '../firebase/types';

export const ONE_SECOND_MS = 1000;
export const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const ONE_WEEK_MS = 7 * ONE_DAY_MS;

/**
 * Returns event timestamp as epoch milliseconds using the stored doc's
 * `timestamp` field and Firestore timestamp helpers.
 */
export function toEpochMs(
  timestamp: FirebaseTimestamp | RawTimestampData,
): number {
  const seconds = getTimestampSeconds(timestamp);
  const nanoseconds = getTimestampNanoseconds(timestamp);
  return seconds * 1000 + nanoseconds / 1e6;
}

/**
 * Formats a millisecond duration as a compact, locale-aware string.
 *
 * Uses `Intl.NumberFormat` with `style: 'unit'` and `unitDisplay: 'short'`
 * for translation-friendly unit labels (e.g., "2 days 3 hr 15 min").
 *
 * @param ms - Duration in milliseconds.
 * @param locale - BCP 47 locale string (e.g., 'en', 'es', 'zh').
 *   Defaults to 'en'.
 * @returns Formatted string, or 'Soon' equivalent when duration <= 0.
 */
export function formatTimeRemaining(ms: number, locale = 'en'): string {
  const totalMinutes = Math.ceil(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const formatUnit = (value: number, unit: string): string =>
    new Intl.NumberFormat(locale, {
      style: 'unit',
      unit,
      unitDisplay: 'short',
    }).format(value);

  const parts: string[] = [];
  if (days > 0) parts.push(formatUnit(days, 'day'));
  if (hours > 0) parts.push(formatUnit(hours, 'hour'));
  if (minutes > 0) parts.push(formatUnit(minutes, 'minute'));

  return parts.length > 0 ? parts.join(' ') : 'Soon';
}

/**
 * Parses the leading `YYYY-MM-DD` of an ISO 8601 string as a local-midnight
 * `Date`. Any time-of-day or timezone-offset suffix is ignored — the returned
 * `Date` represents the named calendar day in the caller's local timezone,
 * not the instant the original string pointed to.
 *
 * Use this when you need to compare a stored ISO date against the user's
 * local calendar (e.g. "is today the same day as the course start?") without
 * the offset baked into the string causing midnight to land on the wrong day.
 *
 * @param iso ISO 8601 date or datetime string. Must contain a leading
 *   `YYYY-MM-DD`; any other shape (including empty/whitespace input) returns
 *   null.
 * @return A `Date` at `00:00:00` local time on the parsed day, or `null`
 *   when the input has no parseable date prefix.
 */
export function parseLocalIsoDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/**
 * Returns a new `Date` at local midnight on the same calendar day as the
 * input. Useful as the canonical anchor for date-only diffs so time-of-day
 * and DST-offset wobble around midnight don't bleed into the result.
 *
 * @param date Source `Date`. Read for its local `getFullYear/Month/Date`
 *   components only.
 * @return A fresh `Date` at `00:00:00` local time on the same calendar day.
 */
export function toLocalDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Whole-day count between two `Date`s, measured in the caller's local
 * timezone. Both inputs are normalized to local midnight before the diff so
 * time-of-day is ignored, and the result is rounded so DST transitions
 * (which produce 23-hour or 25-hour spans) collapse cleanly to ±1 day.
 *
 * Returns a signed integer: positive when `end` is later than `start`,
 * negative when `end` precedes `start`, zero on the same local calendar day
 * (window is closed at both ends — a same-day diff is `0`, not `1`).
 *
 * @param start Earlier anchor. Only its local calendar day is read.
 * @param end Later anchor. Only its local calendar day is read.
 * @return Signed whole-day delta, `end − start`.
 */
export function localDaysBetween(start: Date, end: Date): number {
  return Math.round(
    (toLocalDateOnly(end).getTime() - toLocalDateOnly(start).getTime()) /
      ONE_DAY_MS,
  );
}

/**
 * Formats a Date as "YYYY-MM-DDTHH:MM" in local time, suitable for use as the
 * value of an `<input type="datetime-local">`. Unlike `toISOString()`, which
 * returns UTC, this preserves the user's local timezone.
 */
export function toLocalDatetimeString(date: Date): string {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats an instant for UI labels (e.g. session pickers) using the runtime
 * locale and long date + short time.
 */
export function formatLocaleDateTimeLongShort(
  date: Date,
  locales?: Intl.LocalesArgument,
): string {
  return date.toLocaleString(locales, {
    dateStyle: 'long',
    timeStyle: 'short',
  });
}

/**
 * Formats a duration in whole seconds as compact `Xm Ys`, or `45s` under one minute.
 * Returns an em dash when the value is nullish or NaN.
 */
export function formatSecondsAsMinutesShort(
  seconds: number | null | undefined,
): string {
  if (seconds == null || Number.isNaN(seconds)) {
    return '—';
  }
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m <= 0) {
    return `${String(s)}s`;
  }
  return `${String(m)}m ${String(s)}s`;
}
